import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import Logger from 'core/utils/Logger'
import { TAdditionalData } from 'onboarding/classes/services/api/OnboardingTransitionApi'
import { SimplePageLayout } from 'onboarding/components/layout/simplePageLayout/SimplePageLayout'
import { SimpleResultContent } from 'onboarding/components/page/simpleResultContent/SimpleResultContent'
import { actionTransitionService } from 'onboarding/services/actionTransitionService'
import { onboardingAuthService } from 'onboarding/services/authService'

import { ResultTemplate } from './ResultTemplate'

import styles from './ExecuteTransitionByTokenPage.module.scss'

const MESSAGE_NO_REQUIRED_DATA = 'Required data is not provided'

export const ExecuteTransitionByTokenPage = () => {
  const [isSaving, setIsSaving] = useState(false)
  const [isAuthorizing, setIsAuthorizing] = useState(false)
  const [success, setSuccess] = useState(false)
  const [message, setMessage] = useState('')
  const [additionalData, setAdditionalData] = useState<TAdditionalData | undefined>()
  const { token } = useParams<{ token: string | undefined }>()

  const setSuccessMessage = useCallback((success: boolean, message: string) => {
    setSuccess(success)
    setMessage(message)
  }, [])

  const executeTransitionByToken = useCallback(async () => {
    try {
      setIsSaving(true)
      const result = await actionTransitionService.executeTransitionByToken()

      setAdditionalData(result.data.additionalData)
      setSuccessMessage(result.status === 'success', result.data.message)
    } catch (e: any) {
      Logger.error(e)
      setSuccessMessage(false, e.message || 'Failed to execute transition')
    } finally {
      setIsSaving(false)
    }
  }, [])

  const auth = useCallback(
    async (token: string) => {
      let isAuthorized = false
      try {
        setIsAuthorizing(true)
        if (token) {
          isAuthorized = await onboardingAuthService.authByToken(token)
        }
        setSuccessMessage(isAuthorized, isAuthorized ? 'Authorized successfully' : 'Not Authorized')
      } catch (e: any) {
        Logger.error(e)
        setSuccessMessage(false, e.message || 'Failed to authorize')
      } finally {
        setIsAuthorizing(false)
      }

      return isAuthorized
    },
    [setSuccessMessage]
  )

  useEffect(() => {
    const process = async () => {
      if (token) {
        const isAuthorized = await auth(token)
        if (isAuthorized) {
          await executeTransitionByToken()
        }
      } else {
        setSuccessMessage(false, MESSAGE_NO_REQUIRED_DATA)
      }
    }

    process()
  }, [status, token])

  return (
    <SimplePageLayout>
      <div className={styles.container}>
        <SimpleResultContent
          success={success}
          message={message}
          isLoading={isAuthorizing || isSaving}
        >
          {additionalData ? (
            <ResultTemplate success={success} message={message} additionalData={additionalData} />
          ) : null}
        </SimpleResultContent>
      </div>
    </SimplePageLayout>
  )
}
